import frontendLogger from "../utils/frontendLogger.js";
import useAuthUser from "../hooks/useAuthUser.js";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "../hooks/logout.js";
import { assignWhisky } from "../hooks/forwardToAssign.js";
import { API_BASE_URL } from "../app_config.js";

import "../pages/css/App.css";
import "../pages/css/MainPage.css";
import "../pages/css/Print.css";
import "../pages/css/RecoveryPrint.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import copyStylesheets from "../utils/copyStylesheets.js";

const getAuthToken = () => localStorage.getItem("token");

export default function MainPage() {
  const { username, role } = useAuthUser();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [selectedTasting, setSelectedTasting] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [inviteCreating, setInviteCreating] = useState(false);
  const [inviteInfo, setInviteInfo] = useState("");
  const [releasingTastingId, setReleasingTastingId] = useState(null);
  const ITEMS_PER_PAGE = 5;

  const handleDistilleryDataCreate = () => navigate("/distillery-create");
  const handleTastingCreate = () => navigate("/tasting-create");
  const handleWhiskyCreate = () => navigate("/whisky-create");
  const handleDeleteWhiskys = () => navigate("/whisky-delete");
  const handleDeleteDistilleries = () => navigate("/distillery-delete");
  const handleDeleteTastings = () => navigate("/tasting-delete");
  const handleManageUsers = () => navigate("/manage-users");

  const handleCreateInvite = async () => {
    setInviteCreating(true);
    setInviteInfo("");

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/invite`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.message || "Einladungslink konnte nicht erstellt werden.",
        );
      }

      const FRONTEND_URL =
        process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";

      const inviteLink = `${FRONTEND_URL}/register?invite=${encodeURIComponent(
        data.token,
      )}`;
      const expiry = new Date(data.expiresAt).toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      });

      const popup = window.open("", "", "width=640,height=280");
      if (!popup) {
        setInviteInfo(`Einladungslink: ${inviteLink} (gültig bis ${expiry})`);
        return;
      }

      const doc = popup.document;
      doc.title = "Einladungslink erstellen";
      doc.body.style.fontFamily = "Arial, sans-serif";
      doc.body.style.padding = "20px";
      doc.body.innerHTML = `
        <h2>Einladungslink</h2>
        <p>Der Link ist 15 Minuten gültig und kann nur einmal verwendet werden.</p>
        <p><a href="${inviteLink}" target="_blank">${inviteLink}</a></p>
        <p>Gültig bis: <strong>${expiry}</strong></p>
      `;
    } catch (error) {
      frontendLogger.error("Error creating invite link:", error);
      setInviteInfo(
        error.message || "Fehler beim Erstellen des Einladung-Links.",
      );
    } finally {
      setInviteCreating(false);
    }
  };

  // Recovery-Codes regenerieren
  const handleRegenerateRecoveryCodes = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/auth/regenerateRecoveryCodes`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        throw new Error("Fehler beim Erstellen der Recovery-Codes");
      }

      const data = await response.json();
      frontendLogger.info("New recovery codes generated:", data);

      setRecoveryCodes(data.recoveryCodes || []);
      setShowRecoveryCodes(true);
    } catch (error) {
      frontendLogger.error("Error regenerating recovery codes:", error);
      alert("Fehler: Recovery-Codes konnten nicht erstellt werden");
    }
  };

  // Recovery-Codes drucken
  const handlePrintRecoveryCodes = async () => {
    const printWindow = window.open("", "", "height=400,width=600");
    if (!printWindow) return;

    const codesHTML = recoveryCodes
      .map((code) => `<div class="recovery-code">${code}</div>`)
      .join("");

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Recovery-Codes</title>
        </head>
        <body>
          <div class="popup-content recovery-print-container">
            <h2>Neue Recovery-Codes</h2>
            <p>Speichere diese Codes an einem sicheren Ort. Sie können verwendet werden, um dein Konto zu entsperren, wenn du dein Passwort vergessen hast.</p>
            <div class="codes-box">
              ${codesHTML}
            </div>
            <p class="recovery-note"><strong>Hinweis:</strong> Die bisherigen Recovery-Codes wurden gelöscht.</p>
            <p class="footer">Erstellt am: ${new Date().toLocaleDateString(
              "de-DE",
              {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              },
            )}</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    try {
      await copyStylesheets(document, printWindow.document);
      printWindow.focus();
      printWindow.print();
      frontendLogger.info("Print dialog opened for recovery codes");
    } catch (error) {
      frontendLogger.error("Recovery code print stylesheet error:", error);
      alert("Das Druck-Stylesheet konnte nicht geladen werden.");
    }
  };

  // Tabelle laden
  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/getTastingData`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Fehler beim Laden der Daten");

        const data = await response.json();
        frontendLogger.info("Fetched entries from backend:", data);

        // entries korrekt setzen
        setEntries(data.tastings || []);
      } catch (error) {
        frontendLogger.error("Error fetching entries:", error);
      }
    };

    fetchEntries();
  }, []);

  // Popup Details laden
  const openTasting = async (id) => {
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/getTastingDetailsData/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response.ok) throw new Error("Fehler beim Laden des Tastings");

      const data = await response.json();
      frontendLogger.info("Fetched Tasting details:", data);

      setSelectedTasting({
        id: data.id,
        motto: data.motto || "-",
        place: data.place || "Kein Ort angegeben",
        tasting_date: data.tasting_date || data.date || null,
        released: data.released,
        participants: data.participants || [],
      });
    } catch (error) {
      frontendLogger.error("Error fetching Tasting details:", error);
    }
  };

  const handleReleaseTasting = async (event, id) => {
    event.stopPropagation();
    setReleasingTastingId(id);

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/tastingData/${id}/release`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(
          data.message || "Tasting konnte nicht freigegeben werden",
        );
      }

      setEntries((currentEntries) =>
        currentEntries.map((entry) =>
          entry.id === id ? { ...entry, released: true } : entry,
        ),
      );
    } catch (error) {
      frontendLogger.error("Error releasing tasting:", error);
      alert(error.message || "Fehler beim Freigeben des Tastings");
    } finally {
      setReleasingTastingId(null);
    }
  };

  const filteredEntries = entries.filter(
    (entry) =>
      entry.motto.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (entry.place &&
        entry.place.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  // Paging Logik
  const totalPages = Math.ceil(filteredEntries.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedEntries = filteredEntries.slice(startIndex, endIndex);

  // Zurück auf Seite 1 wenn Search sich ändert
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="page-layout">
      <div className="menu-container">
        <div className="title-box">
          <p>Willkommen bei DRAM</p>
        </div>
        <div className="menu-box">
          <h1>
            {username}{" "}
            <i
              className="bi bi-shield-lock"
              title="Neue Recovery-Codes erstellen"
              onClick={handleRegenerateRecoveryCodes}
              style={{ cursor: "pointer" }}
            ></i>
          </h1>
        </div>

        <nav className="nav">
          <button className="nav-button" onClick={handleDistilleryDataCreate}>
            Destillerie anlegen
          </button>
          <button className="nav-button" onClick={handleWhiskyCreate}>
            Whisky eintragen
          </button>
          <button className="nav-button" onClick={() => assignWhisky(navigate)}>
            Whisky zuordnen
          </button>
          <button className="nav-button" onClick={() => logout(navigate)}>
            Abmelden
          </button>
        </nav>

        <img
          src={require("../assets/logo.png")}
          alt="Versionsinformationen anzeigen"
          className="whisky-image"
          role="button"
          tabIndex={0}
        />
        <div className="footer-link">
          <Link className="privacy-link" to="/privacy">
            Datenschutzerklärung
          </Link>
        </div>
      </div>

      <div className="content-container">
        {role === "admin" && (
          <div className="admin-menu">
            <h3>Admin-Bereich</h3>
            <nav className="admin-nav">
              <button className="admin-button" onClick={handleManageUsers}>
                Benutzer verwalten
              </button>
              <button className="admin-button" onClick={handleDeleteWhiskys}>
                Whiskys löschen
              </button>
              <button
                className="admin-button"
                onClick={handleDeleteDistilleries}
              >
                Destillerien löschen
              </button>
              <button className="admin-button" onClick={handleTastingCreate}>
                Tasting anlegen
              </button>
              <button className="admin-button" onClick={handleDeleteTastings}>
                Tastings löschen
              </button>
              <button
                className="admin-button"
                onClick={handleCreateInvite}
                disabled={inviteCreating}
              >
                {inviteCreating
                  ? "Link wird erstellt..."
                  : "Registrierung auslösen"}
              </button>
            </nav>
            {inviteInfo && <div className="info-message">{inviteInfo}</div>}
          </div>
        )}
        <div className="search-form-group">
          <form id="searchForm">
            <label htmlFor="search">Welches Tasting suchst du?</label>
            <input
              type="text"
              id="search"
              name="search"
              placeholder="Bitte gib ein Suchbegriff an"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </form>
        </div>

        <div className="card-table-container">
          <table className="card-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Motto</th>
                <th>Ort</th>
                <th>Freigabe</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEntries.map((entry) => (
                <tr
                  key={entry.id}
                  className={`table-row clickable-row ${
                    !entry.is_complete ? "disabled-row" : ""
                  }`}
                  onClick={() => openTasting(entry.id)} // Popup immer öffnen
                >
                  <td>
                    {entry.tasting_date
                      ? new Date(entry.tasting_date).toLocaleDateString("de-DE")
                      : "-"}
                  </td>
                  <td>{entry.motto || "-"}</td>
                  <td>{entry.place || "-"}</td>
                  <td>
                    {entry.released ? (
                      "Freigegeben"
                    ) : role === "admin" ? (
                      <button
                        className="admin-button"
                        onClick={(event) =>
                          handleReleaseTasting(event, entry.id)
                        }
                        disabled={releasingTastingId === entry.id}
                      >
                        {releasingTastingId === entry.id
                          ? "Wird freigegeben..."
                          : "Freigeben"}
                      </button>
                    ) : (
                      "Nur dein Whisky sichtbar"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <i className="bi bi-chevron-left"></i> Vorherige
              </button>
              <span className="pagination-info">
                Seite {currentPage} von {totalPages}
              </span>
              <button
                className="pagination-button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Nächste <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedTasting && (
        <div className="popup-overlay">
          <div className="popup-content">
            <button
              className="popup-close"
              onClick={() => setSelectedTasting(null)}
            >
              <i className="bi bi-x-lg"></i>
            </button>

            <h2>{selectedTasting.motto}</h2>
            <p>
              Datum:{" "}
              {selectedTasting.tasting_date
                ? new Date(selectedTasting.tasting_date).toLocaleDateString()
                : "-"}{" "}
              - {selectedTasting.place}
            </p>

            <h3>Teilnehmer und deren Whiskys:</h3>
            {!selectedTasting.released && role !== "admin" && (
              <p>
                Dieses Tasting ist noch nicht freigegeben. Du siehst nur deine
                eigenen Einträge.
              </p>
            )}
            <div>
              {selectedTasting.participants?.length > 0 ? (
                selectedTasting.participants.map((u) => (
                  <div key={u.id}>
                    <strong>{u.username}</strong> -
                    {u.whiskys?.map((w, i) => (
                      <span key={w.id}>
                        {i > 0 && ", "}
                        {w.name} ({w.distillery.name},{" "}
                        {w.alcohol_percent != null
                          ? `${Number(w.alcohol_percent).toFixed(1)}%`
                          : "–"}
                        )
                      </span>
                    ))}
                  </div>
                ))
              ) : (
                <p>Keine Teilnehmer oder Whiskys vorhanden.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showRecoveryCodes && (
        <div className="popup-overlay">
          <div className="popup-content">
            <button
              className="popup-close"
              onClick={() => setShowRecoveryCodes(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>

            <h2>Neue Recovery-Codes</h2>
            <p>
              Speichere diese Codes an einem sicheren Ort. Sie können verwendet
              werden, um dein Konto zu entsperren, wenn du dein Passwort
              vergessen hast.
            </p>

            <div className="codes-box">
              {recoveryCodes.map((code, index) => (
                <div
                  key={index}
                  className="recovery-code"
                >
                  {code}
                </div>
              ))}
            </div>

            <p
              style={{ fontSize: "0.9em", color: "#666", marginBottom: "15px" }}
            >
              <strong>Hinweis:</strong> Die bisherigen Recovery-Codes wurden
              gelöscht.
            </p>

            <button
              style={{
                width: "100%",
                padding: "12px",
                background: "linear-gradient(135deg, #8b5e3c 0%, #5c3a21 100%)",
                color: "#f5e6d3",
                border: "1px solid #c69c6d",
                borderRadius: "5px",
                fontWeight: "600",
                cursor: "pointer",
                fontSize: "14px",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 15px rgba(60, 40, 20, 0.25)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
              onClick={handlePrintRecoveryCodes}
            >
              <i className="bi bi-printer" style={{ marginRight: "8px" }}></i>
              Drucken
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
